import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2 } from 'lucide-react';

// ============================================================
// InlineField — Simple text/url/date/number/email/tel fields
// ============================================================
export function InlineField({
  value,
  field,
  icon: Icon,
  type = 'text',
  placeholder = 'Add...',
  onSave,
  formatDisplay,
  linkHref,
  displayStyle = {}
}) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing) {
      setEditValue(value || '');
    }
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (type !== 'date' && inputRef.current.setSelectionRange) {
        const len = String(inputRef.current.value).length;
        inputRef.current.setSelectionRange(len, len);
      }
    }
  }, [editing, type]);

  const handleSave = async () => {
    const trimmed = type === 'number'
      ? (parseInt(editValue) || 0)
      : (typeof editValue === 'string' ? editValue.trim() : editValue);

    const currentVal = value || (type === 'number' ? 0 : '');
    if (trimmed === currentVal) {
      setEditing(false);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await onSave(field, trimmed);
      setEditing(false);
    } catch {
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      setEditValue(value || '');
      setEditing(false);
      setError(null);
    }
  };

  if (editing) {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
        {Icon && <Icon size={16} className="text-muted" style={{ marginTop: '10px', flexShrink: 0 }} />}
        <div style={{ flex: 1 }}>
          <input
            ref={inputRef}
            type={type}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            className="form-input"
            style={{ padding: '0.375rem 0.625rem', fontSize: '0.9375rem' }}
            disabled={saving}
            placeholder={placeholder}
          />
          {error && <div className="text-xs" style={{ color: 'var(--danger)', marginTop: '0.25rem' }}>{error}</div>}
          {saving && <div className="text-xs text-muted" style={{ marginTop: '0.25rem' }}>Saving...</div>}
        </div>
      </div>
    );
  }

  const displayValue = value
    ? (formatDisplay ? formatDisplay(value) : String(value))
    : null;

  return (
    <div
      className="inline-field"
      onClick={() => setEditing(true)}
      style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}
    >
      {Icon && <Icon size={16} className="text-muted" style={{ marginTop: '2px', flexShrink: 0 }} />}
      <div style={{ flex: 1, minWidth: 0, ...displayStyle }}>
        {displayValue ? (
          linkHref ? (
            <a href={linkHref} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
              {displayValue}
            </a>
          ) : (
            <span>{displayValue}</span>
          )
        ) : (
          <span className="inline-field-placeholder">{placeholder}</span>
        )}
      </div>
    </div>
  );
}


// ============================================================
// InlineTextarea — Multi-line text (notes, relationship_context)
// ============================================================
export function InlineTextarea({
  value,
  field,
  placeholder = 'Add...',
  onSave
}) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!editing) {
      setEditValue(value || '');
    }
  }, [value, editing]);

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus();
      const len = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(len, len);
      // Auto-resize
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [editing]);

  const handleSave = async () => {
    const trimmed = (editValue || '').trim();
    if (trimmed === (value || '')) {
      setEditing(false);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await onSave(field, trimmed);
      setEditing(false);
    } catch {
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setEditValue(value || '');
      setEditing(false);
      setError(null);
    }
    // Enter inserts newline (default behavior for textarea)
  };

  const handleInput = (e) => {
    setEditValue(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = e.target.scrollHeight + 'px';
  };

  if (editing) {
    return (
      <div>
        <textarea
          ref={textareaRef}
          value={editValue}
          onChange={handleInput}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className="form-input form-textarea"
          style={{ padding: '0.375rem 0.625rem', fontSize: '0.9375rem', minHeight: '60px' }}
          disabled={saving}
          placeholder={placeholder}
          rows={2}
        />
        {error && <div className="text-xs" style={{ color: 'var(--danger)', marginTop: '0.25rem' }}>{error}</div>}
        {saving && <div className="text-xs text-muted" style={{ marginTop: '0.25rem' }}>Saving...</div>}
      </div>
    );
  }

  return (
    <div className="inline-field" onClick={() => setEditing(true)}>
      {value ? (
        <span style={{ whiteSpace: 'pre-wrap' }}>{value}</span>
      ) : (
        <span className="inline-field-placeholder">{placeholder}</span>
      )}
    </div>
  );
}


// ============================================================
// InlineSelect — Category picker with colored buttons
// ============================================================
export function InlineSelect({
  value,
  field,
  options, // [{value, label, color}]
  onSave
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!editing) return;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setEditing(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [editing]);

  const handleSelect = async (newValue) => {
    if (newValue === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(field, newValue);
      setEditing(false);
    } catch {
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const currentOption = options.find(o => o.value === value) || options[0];

  if (editing) {
    return (
      <div ref={wrapperRef}>
        <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
          {options.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => handleSelect(opt.value)}
              disabled={saving}
              style={{
                padding: '0.25rem 0.625rem',
                borderRadius: '9999px',
                border: '2px solid',
                borderColor: value === opt.value ? opt.color : 'var(--gray-200)',
                background: value === opt.value ? `${opt.color}15` : 'transparent',
                color: value === opt.value ? opt.color : 'var(--gray-600)',
                fontSize: '0.8125rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {error && <div className="text-xs" style={{ color: 'var(--danger)', marginTop: '0.25rem' }}>{error}</div>}
      </div>
    );
  }

  return (
    <div className="inline-field" onClick={() => setEditing(true)} style={{ display: 'inline-flex' }}>
      <span
        className="badge"
        style={{
          background: `${currentOption.color}15`,
          color: currentOption.color,
          textTransform: 'capitalize',
          cursor: 'pointer'
        }}
      >
        {currentOption.label}
      </span>
    </div>
  );
}


// ============================================================
// InlineSlider — Relationship strength (0-100)
// ============================================================
export function InlineSlider({
  value,
  field,
  onSave
}) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || 0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!editing) {
      setEditValue(value || 0);
    }
  }, [value, editing]);

  useEffect(() => {
    if (!editing) return;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        handleSave();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [editing, editValue]);

  const getStrengthColor = (s) => {
    if (s >= 70) return 'var(--success)';
    if (s >= 40) return 'var(--warning)';
    return 'var(--danger)';
  };

  const handleSave = async () => {
    if (editValue === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(field, editValue);
      setEditing(false);
    } catch {
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div ref={wrapperRef}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <span className="text-sm">Strength</span>
          <span className="font-medium">{editValue}%</span>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          value={editValue}
          onChange={(e) => setEditValue(parseInt(e.target.value))}
          onMouseUp={handleSave}
          onTouchEnd={handleSave}
          style={{ width: '100%' }}
        />
        <div className="strength-bar" style={{ marginTop: '0.5rem', height: '8px' }}>
          <div className="strength-fill" style={{ width: `${editValue}%`, background: getStrengthColor(editValue) }} />
        </div>
        {error && <div className="text-xs" style={{ color: 'var(--danger)', marginTop: '0.25rem' }}>{error}</div>}
      </div>
    );
  }

  return (
    <div className="inline-field" onClick={() => setEditing(true)}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <span className="text-sm">Strength</span>
        <span className="font-medium">{value}%</span>
      </div>
      <div className="strength-bar" style={{ height: '8px' }}>
        <div className="strength-fill" style={{ width: `${value}%`, background: getStrengthColor(value) }} />
      </div>
    </div>
  );
}


// ============================================================
// InlineArrayField — Emails and phones arrays
// ============================================================
export function InlineArrayField({
  values,
  field,
  legacyField,
  icon: Icon,
  typeOptions,
  inputType = 'text',
  placeholder = 'Add...',
  linkPrefix,
  onSave
}) {
  const [editingIndex, setEditingIndex] = useState(null); // index or -1 for "new"
  const [editValue, setEditValue] = useState('');
  const [editType, setEditType] = useState(typeOptions[0]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editingIndex !== null && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editingIndex]);

  const startEditing = (index) => {
    const item = values[index];
    setEditValue(item.value);
    setEditType(item.type);
    setEditingIndex(index);
    setError(null);
  };

  const startAdding = () => {
    setEditValue('');
    setEditType(typeOptions[0]);
    setEditingIndex(-1);
    setError(null);
  };

  const saveArray = async (newArray) => {
    setSaving(true);
    setError(null);
    try {
      await onSave({
        [field]: newArray,
        [legacyField]: newArray[0]?.value || ''
      });
      setEditingIndex(null);
    } catch {
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveItem = () => {
    const trimmedValue = editValue.trim();
    let newArray;

    if (editingIndex === -1) {
      if (!trimmedValue) {
        setEditingIndex(null);
        return;
      }
      newArray = [...(values || []), { value: trimmedValue, type: editType }];
    } else {
      if (!trimmedValue) {
        newArray = (values || []).filter((_, i) => i !== editingIndex);
      } else {
        newArray = (values || []).map((item, i) =>
          i === editingIndex ? { value: trimmedValue, type: editType } : item
        );
      }
    }

    saveArray(newArray);
  };

  const handleDeleteItem = (e, index) => {
    e.stopPropagation();
    const newArray = (values || []).filter((_, i) => i !== index);
    saveArray(newArray);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveItem();
    }
    if (e.key === 'Escape') {
      setEditingIndex(null);
      setError(null);
    }
  };

  const items = values || [];

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
      {Icon && <Icon size={16} className="text-muted" style={{ marginTop: '2px', flexShrink: 0 }} />}
      <div style={{ flex: 1 }}>
        {items.length > 0 ? (
          items.map((item, i) => (
            editingIndex === i ? (
              <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <input
                  ref={inputRef}
                  type={inputType}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onBlur={handleSaveItem}
                  onKeyDown={handleKeyDown}
                  className="form-input"
                  style={{ flex: 1, padding: '0.375rem 0.625rem' }}
                  placeholder={placeholder}
                  disabled={saving}
                />
                <select
                  value={editType}
                  onChange={(e) => setEditType(e.target.value)}
                  className="form-input form-select"
                  style={{ width: '90px', padding: '0.375rem' }}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onMouseDown={(e) => { e.preventDefault(); handleDeleteItem(e, i); }}
                  style={{ color: 'var(--danger)' }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ) : (
              <div
                key={i}
                className="inline-field"
                onClick={() => startEditing(i)}
                style={{ marginBottom: i < items.length - 1 ? '0.25rem' : 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <a href={`${linkPrefix}${item.value}`} onClick={e => e.stopPropagation()}>
                  {item.value}
                </a>
                <span className="text-xs text-muted">({item.type})</span>
              </div>
            )
          ))
        ) : editingIndex !== -1 ? (
          <div className="inline-field" onClick={startAdding}>
            <span className="inline-field-placeholder">{placeholder}</span>
          </div>
        ) : null}

        {/* "Add new" input when editingIndex === -1 */}
        {editingIndex === -1 && (
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: items.length > 0 ? '0.5rem' : 0 }}>
            <input
              ref={inputRef}
              type={inputType}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleSaveItem}
              onKeyDown={handleKeyDown}
              className="form-input"
              style={{ flex: 1, padding: '0.375rem 0.625rem' }}
              placeholder={placeholder}
              disabled={saving}
            />
            <select
              value={editType}
              onChange={(e) => setEditType(e.target.value)}
              className="form-input form-select"
              style={{ width: '90px', padding: '0.375rem' }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        )}

        {/* "Add" button when not in add mode and there are existing items */}
        {items.length > 0 && editingIndex === null && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={startAdding}
            style={{ marginTop: '0.25rem', padding: '0.125rem 0.5rem', fontSize: '0.75rem' }}
          >
            <Plus size={12} /> Add
          </button>
        )}

        {error && <div className="text-xs" style={{ color: 'var(--danger)', marginTop: '0.25rem' }}>{error}</div>}
      </div>
    </div>
  );
}


// ============================================================
// InlineTagEditor — Tag selection dropdown
// ============================================================
export function InlineTagEditor({
  selectedTags, // [{id, name, color}, ...]
  allTags,      // [{id, name, color}, ...]
  onSave        // async (tagIds) => void
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!editing) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setEditing(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [editing]);

  const selectedIds = (selectedTags || []).map(t => typeof t === 'string' ? t : t?.id).filter(Boolean);

  const handleToggleTag = async (tagId) => {
    const newIds = selectedIds.includes(tagId)
      ? selectedIds.filter(id => id !== tagId)
      : [...selectedIds, tagId];

    setSaving(true);
    try {
      await onSave(newIds);
    } catch {
      // error handled silently; tags will revert on next re-render
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
      {(selectedTags || []).map(tag => (
        <span
          key={tag.id || tag}
          className="tag"
          style={{ background: `${tag.color}20`, color: tag.color }}
        >
          {tag.name}
        </span>
      ))}
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={() => setEditing(!editing)}
        style={{ padding: '0.125rem 0.375rem', borderRadius: '0.375rem' }}
      >
        <Plus size={14} />
      </button>

      {editing && allTags && allTags.length > 0 && (
        <div
          ref={dropdownRef}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: '0.375rem',
            background: 'white',
            border: '1px solid var(--gray-200)',
            borderRadius: '0.5rem',
            padding: '0.75rem',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 10,
            minWidth: '200px',
            maxWidth: '320px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.375rem'
          }}
        >
          {allTags.map(tag => {
            const isSelected = selectedIds.includes(tag.id);
            return (
              <button
                key={tag.id}
                type="button"
                onClick={() => handleToggleTag(tag.id)}
                disabled={saving}
                style={{
                  padding: '0.25rem 0.625rem',
                  borderRadius: '9999px',
                  border: '1px solid',
                  borderColor: isSelected ? tag.color : 'var(--gray-300)',
                  background: isSelected ? tag.color : 'transparent',
                  color: isSelected ? 'white' : 'var(--gray-600)',
                  fontSize: '0.8125rem',
                  cursor: 'pointer',
                  transition: 'all 0.15s'
                }}
              >
                {tag.name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
